<template>
    <header class="app-header" :class="{ 'header-fixed': isScrolled }">
        <div class="container">
            <div class="header-content">
                <router-link to="/" class="logo">
                    <img src="/brand-mark.svg" alt="云轩网络" width="40" height="40" class="logo-mark" />
                    <span class="company-name">云轩网络</span>
                </router-link>

                <!-- 移动端菜单按钮 -->
                <div class="menu-toggle" @click="toggleMenu">
                    <el-icon :size="24">
                        <component :is="isMenuOpen ? 'Close' : 'Menu'" />
                    </el-icon>
                </div>

                <!-- 导航菜单 -->
                <nav class="nav-menu" :class="{ 'menu-open': isMenuOpen }">
                    <template v-for="item in navItems" :key="item.name">
                        <router-link v-if="item.type === 'link'" :to="item.path" class="nav-item"
                            :class="{ active: currentPath === item.path }" @click="closeMenu">
                            {{ item.name }}
                        </router-link>
                        <a v-else-if="item.type === 'external'" :href="item.href" class="nav-item" target="_blank"
                            rel="noopener noreferrer" @click="closeMenu">
                            {{ item.name }}
                        </a>
                        <div v-else class="nav-dropdown" :class="{ 'dropdown-open': productsDropdownOpen }">
                            <span class="nav-dropdown-trigger" @click.stop="onProductsTriggerClick">
                                {{ item.name }}
                                <span class="nav-dropdown-chevron" aria-hidden="true">▾</span>
                            </span>
                            <div class="nav-dropdown-panel" role="menu">
                                <a v-for="child in item.children.filter((child) => child.visible !== false)" :key="child.href"
                                    :href="child.href" class="nav-dropdown-link"
                                    target="_blank" rel="noopener noreferrer" role="menuitem" @click="onExternalProductClick">
                                    {{ child.name }}
                                </a>
                            </div>
                        </div>
                    </template>
                </nav>
            </div>
        </div>
    </header>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRoute } from 'vue-router'

/** 云枢键达对外落地页 */
const PASTE_BRIDGE_LANDING = 'https://pb.yunxuanwl.cn/'
/** 日志管理系统对外落地页 */
const LOG_MANAGER_LANDING = 'https://log.yunxuanwl.cn/'
/** 黄鸟人际产品落地页 */
const ORIOLE_LANDING = 'https://hn.yunxuanwl.cn/'
/** 飞书与 Telegram 消息推送公共落地页 */
const MESSAGE_PUSH_LANDING = 'https://bot.yunxuanwl.cn/'
/** 企业服务公开站 */
const ENTERPRISE_SERVICES_LANDING = 'https://es.yunxuanwl.cn/zh-cn/'
/** AI 智包官网 */
const AI_ZHIBAO_LANDING = 'https://ai-zhibao.cn/'

type NavLinkItem = { type: 'link'; name: string; path: string }
type NavExternalItem = { type: 'external'; name: string; href: string }
type NavDropdownItem = {
    type: 'dropdown'
    name: string
    children: { name: string; href: string; visible?: boolean }[]
}

const route = useRoute()
const isMenuOpen = ref(false)
const isScrolled = ref(false)
const productsDropdownOpen = ref(false)

const navItems: (NavLinkItem | NavExternalItem | NavDropdownItem)[] = [
    { type: 'link', name: '首页', path: '/' },
    { type: 'link', name: '业务范围', path: '/business' },
    { type: 'external', name: '企业服务', href: ENTERPRISE_SERVICES_LANDING },
    { type: 'link', name: '成功案例', path: '/success-cases' },
    {
        type: 'dropdown',
        name: '产品列表',
        children: [
            { name: '云枢键达', href: PASTE_BRIDGE_LANDING },
            { name: '日志管理', href: LOG_MANAGER_LANDING },
            { name: '黄鸟人际', href: ORIOLE_LANDING, visible: false },
            { name: '消息推送', href: MESSAGE_PUSH_LANDING },
            { name: 'AI 智包', href: AI_ZHIBAO_LANDING },
        ],
    },
    { type: 'link', name: '合作伙伴', path: '/partners' },
    { type: 'link', name: '关于我们', path: '/about' },
    { type: 'link', name: '联系我们', path: '/contact' },
]

const currentPath = computed(() => route.path)

const isMobileNav = () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches

function onProductsTriggerClick() {
    if (isMobileNav()) {
        productsDropdownOpen.value = !productsDropdownOpen.value
    }
}

function onExternalProductClick() {
    productsDropdownOpen.value = false
    closeMenu()
}

const toggleMenu = () => {
    isMenuOpen.value = !isMenuOpen.value
    if (!isMenuOpen.value) {
        productsDropdownOpen.value = false
    }
    if (isMenuOpen.value) {
        document.body.style.overflow = 'hidden'
    } else {
        document.body.style.overflow = ''
    }
}

const closeMenu = () => {
    isMenuOpen.value = false
    productsDropdownOpen.value = false
    document.body.style.overflow = ''
}

const handleScroll = () => {
    isScrolled.value = window.scrollY > 0
}

const handleClickOutside = (e: MouseEvent) => {
    const target = e.target as HTMLElement
    if (!target.closest('.nav-dropdown')) {
        productsDropdownOpen.value = false
    }
}

onMounted(() => {
    window.addEventListener('scroll', handleScroll)
    document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll)
    document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped lang="scss">
.app-header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    background: #fff;
    transition: all 0.3s ease;

    &.header-fixed {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    .header-content {
        display: flex;
        align-items: center;
        justify-content: space-between;
        height: 80px;
    }

    .logo {
        display: flex;
        align-items: center;
        gap: 10px;
        text-decoration: none;
        color: #333;

        .logo-mark {
            width: 40px;
            height: 40px;
            flex-shrink: 0;
            display: block;
            transition: transform 0.35s ease;
        }

        .company-name {
            font-family: 'PingFang SC', 'Microsoft YaHei', Arial, sans-serif;
            font-size: 22px;
            font-weight: 600;
            letter-spacing: 1px;
            color: #2c3e50;
            transition: color 0.3s ease;
            line-height: 1;
            white-space: nowrap;
        }

        &:hover {
            .logo-mark {
                transform: rotate(360deg);
            }

            .company-name {
                color: var(--el-color-primary);
            }
        }
    }

    .menu-toggle {
        display: none;
        cursor: pointer;
    }

    .nav-menu {
        display: flex;
        align-items: center;
        gap: 30px;

        .nav-item {
            text-decoration: none;
            color: #333;
            font-size: 16px;
            padding: 8px 0;
            position: relative;
            transition: color 0.3s;

            &::after {
                content: '';
                position: absolute;
                bottom: 0;
                left: 0;
                width: 0;
                height: 2px;
                background: var(--el-color-primary);
                transition: width 0.3s;
            }

            &:hover,
            &.active {
                color: var(--el-color-primary);

                &::after {
                    width: 100%;
                }
            }
        }
    }

    .nav-dropdown {
        position: relative;
        padding: 8px 0;

        .nav-dropdown-trigger {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            cursor: default;
            color: #333;
            font-size: 16px;
            user-select: none;
            transition: color 0.3s;

            .nav-dropdown-chevron {
                font-size: 10px;
                opacity: 0.65;
                transition: transform 0.25s ease;
            }
        }

        .nav-dropdown-panel {
            position: absolute;
            top: 100%;
            left: 50%;
            transform: translateX(-50%) translateY(4px);
            min-width: 168px;
            padding: 6px 0;
            background: #fff;
            border-radius: 8px;
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
            opacity: 0;
            visibility: hidden;
            pointer-events: none;
            transition:
                opacity 0.2s ease,
                visibility 0.2s ease,
                transform 0.2s ease;
            z-index: 1001;
        }

        .nav-dropdown-link {
            display: block;
            padding: 10px 18px;
            font-size: 15px;
            color: #333;
            text-decoration: none;
            transition:
                background 0.15s ease,
                color 0.15s ease;

            &:hover {
                background: rgba(64, 158, 255, 0.08);
                color: var(--el-color-primary);
            }
        }

        @media (hover: hover) and (pointer: fine) {
            &:hover {
                .nav-dropdown-trigger {
                    color: var(--el-color-primary);
                }

                .nav-dropdown-chevron {
                    transform: rotate(-180deg);
                }

                .nav-dropdown-panel {
                    opacity: 1;
                    visibility: visible;
                    pointer-events: auto;
                    transform: translateX(-50%) translateY(0);
                }
            }
        }
    }
}

@media (max-width: 768px) {
    .app-header {
        .header-content {
            height: 60px;
        }

        .logo {
            gap: 8px;

            .logo-mark {
                width: 32px;
                height: 32px;
            }

            .company-name {
                font-size: 18px;
            }
        }

        .menu-toggle {
            display: block;
        }

        .nav-menu {
            position: fixed;
            top: 60px;
            left: 0;
            right: 0;
            bottom: 0;
            background: #fff;
            flex-direction: column;
            padding: 20px;
            transform: translateX(100%);
            transition: transform 0.3s;
            gap: 12px;
            align-items: stretch;

            &.menu-open {
                transform: translateX(0);
            }

            .nav-item {
                font-size: 18px;
                width: 100%;
                text-align: center;
                padding: 15px 0;
            }
        }

        .nav-dropdown {
            padding: 0;
            width: 100%;
            text-align: center;

            .nav-dropdown-trigger {
                justify-content: center;
                width: 100%;
                cursor: pointer;
                padding: 15px 0;
                font-size: 18px;
            }

            .nav-dropdown-panel {
                position: static;
                transform: none;
                box-shadow: none;
                border-radius: 0;
                max-height: 0;
                overflow: hidden;
                opacity: 1;
                visibility: visible;
                padding: 0;
                pointer-events: none;
                transition: max-height 0.28s ease;
            }

            &.dropdown-open {
                .nav-dropdown-panel {
                    max-height: 240px;
                    pointer-events: auto;
                }

                .nav-dropdown-chevron {
                    transform: rotate(-180deg);
                }
            }

            .nav-dropdown-link {
                padding: 12px 16px;
            }
        }
    }
}
</style>
